/** @file COMMAND_HANDLERS: the definition of every command name (the exhaustiveness test pins the keys). */
import { attachmentAttachCommand } from '../attachment/attach.command';
import { attachmentDetachCommand } from '../attachment/detach.command';
import { attachmentSetCommand } from '../attachment/set.command';
import { clipBlendPosesCommand } from '../clip/blendPoses.command';
import { clipCopyPoseCommand } from '../clip/copyPose.command';
import { clipCreateCommand } from '../clip/create.command';
import { clipDeleteCommand } from '../clip/delete.command';
import { clipImportCommand } from '../clip/import.command';
import { clipKeyAllCommand } from '../clip/keyAll.command';
import { clipMirrorHalfCommand } from '../clip/mirrorHalf.command';
import { clipMirrorPoseCommand } from '../clip/mirrorPose.command';
import { clipRenameCommand } from '../clip/rename.command';
import { clipSetEventsCommand } from '../clip/setEvents.command';
import { clipSetKeysCommand } from '../clip/setKeys.command';
import { clipSetMetaCommand } from '../clip/setMeta.command';
import { clipSetPoseCommand } from '../clip/setPose.command';
import { clipSetTrackCommand } from '../clip/setTrack.command';
import { clipShiftFramesCommand } from '../clip/shiftFrames.command';
import { drawableAddCommand } from '../drawable/add.command';
import { drawableRemoveCommand } from '../drawable/remove.command';
import { drawableSetCommand } from '../drawable/set.command';
import { facetAddEntryCommand } from '../facet/addEntry.command';
import { facetRemoveEntryCommand } from '../facet/removeEntry.command';
import { facetSetDrawnAnglesCommand } from '../facet/setDrawnAngles.command';
import { facetSetEntryCommand } from '../facet/setEntry.command';
import { facetSetTableCommand } from '../facet/setTable.command';
import { imagesRescaleCommand } from '../images/rescale.command';
import { libraryApplyCommand } from '../library/apply.command';
import { nodeAddCommand } from '../node/add.command';
import { nodeMirrorPairCommand } from '../node/mirrorPair.command';
import { nodeRemoveCommand } from '../node/remove.command';
import { nodeRenameCommand } from '../node/rename.command';
import { nodeReparentCommand } from '../node/reparent.command';
import { nodeSetCommand } from '../node/set.command';
import { puppetCreateCommand } from '../puppet/create.command';
import { puppetImportCommand } from '../puppet/import.command';
import { puppetRenameCommand } from '../puppet/rename.command';
import { puppetSetBoundsCommand } from '../puppet/setBounds.command';
import { puppetSetFacingCommand } from '../puppet/setFacing.command';
import { puppetSetProfileCommand } from '../puppet/setProfile.command';
import { sheetPackCommand } from '../sheet/pack.command';
import { sheetUploadCommand } from '../sheet/upload.command';
import { slotClearCommand } from '../slot/clear.command';
import { slotFillCommand } from '../slot/fill.command';
import { slotFillManyCommand } from '../slot/fillMany.command';
import { slotFitPivotCommand } from '../slot/fitPivot.command';
import { slotRenderMaskCommand } from '../slot/renderMask.command';
import { socketSetCommand } from '../socket/set.command';
import { templateInstantiateCommand } from '../template/instantiate.command';
import { variantAddCommand } from '../variant/add.command';
import { variantRemoveCommand } from '../variant/remove.command';
import type { CommandName } from '../types/command';
import type { CommandDef } from './commandDef';

/** Every command's definition, keyed by its name. */
export const COMMAND_HANDLERS: { [N in CommandName]: CommandDef<N> } = {
  'puppet.create': puppetCreateCommand,
  'puppet.rename': puppetRenameCommand,
  'puppet.setProfile': puppetSetProfileCommand,
  'puppet.setFacing': puppetSetFacingCommand,
  'puppet.setBounds': puppetSetBoundsCommand,
  'puppet.import': puppetImportCommand,
  'node.add': nodeAddCommand,
  'node.remove': nodeRemoveCommand,
  'node.set': nodeSetCommand,
  'node.reparent': nodeReparentCommand,
  'node.mirrorPair': nodeMirrorPairCommand,
  'node.rename': nodeRenameCommand,
  'drawable.add': drawableAddCommand,
  'drawable.remove': drawableRemoveCommand,
  'drawable.set': drawableSetCommand,
  'facet.addEntry': facetAddEntryCommand,
  'facet.removeEntry': facetRemoveEntryCommand,
  'facet.setEntry': facetSetEntryCommand,
  'facet.setTable': facetSetTableCommand,
  'facet.setDrawnAngles': facetSetDrawnAnglesCommand,
  'slot.fill': slotFillCommand,
  'slot.fillMany': slotFillManyCommand,
  'slot.clear': slotClearCommand,
  'slot.renderMask': slotRenderMaskCommand,
  'slot.fitPivot': slotFitPivotCommand,
  'sheet.upload': sheetUploadCommand,
  'sheet.pack': sheetPackCommand,
  'images.rescale': imagesRescaleCommand,
  'variant.add': variantAddCommand,
  'variant.remove': variantRemoveCommand,
  'socket.set': socketSetCommand,
  'attachment.attach': attachmentAttachCommand,
  'attachment.detach': attachmentDetachCommand,
  'attachment.set': attachmentSetCommand,
  'template.instantiate': templateInstantiateCommand,
  'clip.create': clipCreateCommand,
  'clip.delete': clipDeleteCommand,
  'clip.rename': clipRenameCommand,
  'clip.setMeta': clipSetMetaCommand,
  'clip.setKeys': clipSetKeysCommand,
  'clip.setPose': clipSetPoseCommand,
  'clip.copyPose': clipCopyPoseCommand,
  'clip.mirrorPose': clipMirrorPoseCommand,
  'clip.blendPoses': clipBlendPosesCommand,
  'clip.keyAll': clipKeyAllCommand,
  'clip.shiftFrames': clipShiftFramesCommand,
  'clip.mirrorHalf': clipMirrorHalfCommand,
  'clip.setTrack': clipSetTrackCommand,
  'clip.setEvents': clipSetEventsCommand,
  'clip.import': clipImportCommand,
  'library.apply': libraryApplyCommand,
};
